import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Box, FormControl, Typography, useTheme } from '@mui/material';
import { StatusFilter } from 'app/shared/model/enumerations/status-filter.model';
import axios from 'axios';
import React from 'react';
import { ClearIndicatorProps, DropdownIndicatorProps, components } from 'react-select';
import { AsyncPaginate } from 'react-select-async-paginate';

interface Props {
  sortQuery?: string;
  value: any;
  options?: any[];
  apiUrl?: string;
  valueName?: string;
  isRequired?: boolean; // ToDo: remover
  searchName?: string;
  placeholder?: string;
  isOptionDisabled?: any;
  label?: string;
  filterStyle?: boolean; // ToDo: remover
  statusFilter?: boolean;
  searchRepresentative?: boolean;
  icon?: IconProp;
  isDisabled?: boolean;
  isClearable?: boolean;
  debounceTimeout?: number;
  onChange: (value: any) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  displayFormat?: (option: any) => string;
  additionalOptions?: any[];
  staticOptions?: any[];
  minWidth?: number | string;
  desktopMinWidth?: number | string;
  controlHeight?: number | string;
  controlFontSize?: number | string;
  menuPortalTarget?: HTMLElement | null;
  excludeOptionIds?: Array<number | string>;
  autoFocus?: boolean;
  openMenuOnFocus?: boolean;
  defaultMenuIsOpen?: boolean;
}

const SelectPaginate: React.FC<Props> = ({
  sortQuery,
  value,
  options,
  apiUrl = '',
  placeholder = '',
  onChange,
  valueName = 'name',
  searchName = 'search',
  searchRepresentative,
  onKeyDown,
  isOptionDisabled,
  label,
  statusFilter = false,
  icon,
  isDisabled = false,
  isClearable = false,
  debounceTimeout = 500,
  displayFormat,
  additionalOptions = [],
  staticOptions,
  minWidth = '250px',
  desktopMinWidth = '300px',
  controlHeight,
  controlFontSize,
  menuPortalTarget,
  excludeOptionIds = [],
  autoFocus,
  openMenuOnFocus,
  defaultMenuIsOpen,
}) => {
  const theme = useTheme();

  const [hasValue, setHasValue] = React.useState<boolean>(false);
  const hasSelectedValue = Boolean(value);
  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      minWidth,
      borderRadius: '8px',
      backgroundColor: 'transparent',
      color: theme.palette.text.secondary,
      boxShadow: 'none',
      ...(controlFontSize ? { fontSize: controlFontSize } : {}),
      ...(controlHeight ? { height: controlHeight, minHeight: controlHeight, padding: '0 10px' } : { padding: '3px 10px' }),
      borderColor: '#808080',

      '&:hover': {
        borderColor: theme.palette.text.primary,
      },
      '&:focus': {
        borderColor: theme.palette.secondary.main,
      },
      '@media (min-width: 900px)': {
        minWidth: desktopMinWidth,
      },
    }),
    container: (provided: any) => ({
      ...provided,
      width: '100%',
      minWidth: 0,
      maxWidth: '100%',
    }),
    menu: (provided: any) => ({
      ...provided,
      color: theme.palette.text.primary,
      padding: '6px',
      backgroundColor: theme.palette.background.paper,
      backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.12));',
      boxShadow: '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
    }),
    menuList: (provided: any) => ({
      ...provided,
    }),
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: theme.zIndex.modal + 1,
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: '#757575',
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      ...(controlFontSize ? { fontSize: controlFontSize } : {}),
    }),
    option: (provided: any, state: { isFocused: boolean; isSelected: boolean }) => ({
      ...provided,
      backgroundColor: state.isSelected ? theme.palette.action.selected : state.isFocused ? theme.palette.action.hover : 'transparent',
      color: theme.palette.text.primary,
    }),
    input: (provided: any) => ({
      ...provided,
      color: theme.palette.text.primary,
      ...(controlFontSize ? { fontSize: controlFontSize } : {}),
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      minWidth: 0,
      overflow: 'hidden',
      ...(controlHeight ? { height: controlHeight, paddingTop: 0, paddingBottom: 0 } : {}),
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      ...(controlHeight ? { height: controlHeight } : {}),
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: theme.palette.text.primary,
      maxWidth: 'calc(100% - 8px)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      ...(controlFontSize ? { fontSize: controlFontSize } : {}),
    }),
    clearIndicator: (provided: any) => ({
      ...provided,
      alignItems: 'center',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'center',
      padding: 0,
      width: 32,
    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      display: isClearable && hasSelectedValue ? 'none' : provided.display,
    }),
  };

  const DropdownIndicator = (props: DropdownIndicatorProps) => {
    if (!icon) {
      return null;
    }

    return (
      <components.DropdownIndicator {...props}>
        <FontAwesomeIcon icon={icon} />
      </components.DropdownIndicator>
    );
  };

  const ClearIndicator = (props: ClearIndicatorProps) => (
    <components.ClearIndicator {...props}>
      <Box
        component="div"
        sx={{
          alignItems: 'center',
          backgroundColor: theme.palette.action.hover,
          borderRadius: '50%',
          color: theme.palette.text.secondary,
          display: 'inline-flex',
          height: 22,
          justifyContent: 'center',
          minHeight: '22px !important',
          transition: theme.transitions.create(['background-color', 'color']),
          width: 22,
          '&:hover': {
            backgroundColor: theme.palette.action.selected,
            color: theme.palette.text.primary,
          },
        }}
      >
        <CloseRoundedIcon
          sx={{
            color: 'inherit',
            fontSize: 16,
            marginRight: '0 !important',
            width: '16px !important',
          }}
        />
      </Box>
    </components.ClearIndicator>
  );

  const loadOptions = async (searchQuery: string, loadedOptions: any, additional?: { page: number }) => {
    const page = additional?.page ?? 1;

    if (staticOptions) {
      const normalizedSearch = searchQuery.trim().toLowerCase();

      const filteredOptions = normalizedSearch
        ? staticOptions.filter(option => getOptionLabel(option)?.toLowerCase().includes(normalizedSearch))
        : staticOptions;

      return {
        options: filteredOptions.filter(option => !excludeOptionIds.includes(getOptionValue(option))),
        hasMore: false,
        additional: {
          page: page + 1,
        },
      };
    }

    if (!apiUrl) {
      const normalizedSearch = searchQuery.trim().toLowerCase();

      const filtered = (options || []).filter(opt => {
        const optionLabel = typeof opt === 'string' ? opt : opt?.[valueName] ?? opt?.name ?? opt?.id;

        return optionLabel?.toString().toLowerCase().includes(normalizedSearch);
      });

      return {
        options: filtered.filter(option => !excludeOptionIds.includes(getOptionValue(option))),
        hasMore: false,
        additional: {
          page: page + 1,
        },
      };
    }

    try {
      const { data } = await axios.get(apiUrl, {
        params: {
          page: page - 1,
          size: 10,
          sort: sortQuery || valueName,
          [searchName]: searchQuery,
          searchRepresentative,
          cacheBuster: new Date().getTime(),
          ...(statusFilter && { status: StatusFilter.ALL }),
        },
      });

      const result = page === 1 && data.length > 0 ? [...additionalOptions, ...data] : data;

      return {
        options: result.filter(option => !excludeOptionIds.includes(getOptionValue(option))),
        hasMore: data.length > 0,
        additional: {
          page: page + 1,
        },
      };
    } catch (error) {
      throw new Error('Falha ao carregar as opções: ' + error);
    }
  };

  const getOptionValue = (option: any) => option?.id;
  const getOptionLabel = (option: any) => {
    if (!option) return '';

    if (displayFormat) {
      return displayFormat(option);
    }

    return option?.[valueName] ?? option?.name ?? String(option);
  };

  const handleChange = (selectedOption: any) => {
    onChange(selectedOption?.id != null ? selectedOption : null);
  };

  const selectComponents: any = {
    ...(icon && !(isClearable && hasSelectedValue) ? { DropdownIndicator } : {}),
    ...(isClearable ? { ClearIndicator } : {}),
    ...(isClearable && hasSelectedValue ? { DropdownIndicator: null } : {}),
  };

  return (
    <Box>
      <FormControl sx={{ width: '100%' }}>
        {label && (
          <Typography color={theme.palette.text.primary} fontWeight="600" fontSize="16px" textAlign="left">
            {label}
          </Typography>
        )}
        <Box sx={{ width: '100%', minWidth: 0 }}>
          <AsyncPaginate
            onKeyDown={onKeyDown}
            loadingMessage={() => 'Carregando...'}
            noOptionsMessage={() => 'Nenhum resultado encontrado'}
            className={(hasValue || hasSelectedValue) && value ? 'input-react-select-async-paginate-with-value' : 'input-react-select-async-paginate'}
            value={value || ''}
            loadOptions={loadOptions}
            getOptionValue={getOptionValue}
            getOptionLabel={getOptionLabel}
            onChange={handleChange}
            isSearchable
            isClearable={isClearable}
            placeholder={placeholder}
            isOptionDisabled={isOptionDisabled}
            components={selectComponents}
            additional={{
              page: 1,
            }}
            debounceTimeout={debounceTimeout}
            isDisabled={isDisabled}
            styles={customStyles}
            menuPortalTarget={menuPortalTarget}
            menuPosition={menuPortalTarget ? 'fixed' : 'absolute'}
            cacheUniqs={[excludeOptionIds.join(',')]}
            autoFocus={autoFocus}
            openMenuOnFocus={openMenuOnFocus}
            defaultMenuIsOpen={defaultMenuIsOpen}
          />
        </Box>
      </FormControl>
    </Box>
  );
};

export default SelectPaginate;
